import type Phaser from "phaser";
import { createShellSystems, type ShellSystems } from "../app/createShellSystems.js";
import type { GameContext } from "../app/GameContext.js";
import type { DomainControllers } from "../app/createControllers.js";
import type { FeatureFlags } from "../env/featureFlags.js";
import type { PieceRandomSource } from "../data/modulePieces.js";
import type { ModuleBrickSaveData } from "../models/ModuleBrickSaveData.js";
import type { DailyResetService } from "@yjsgames/daily-reset";
import type { CooldownService } from "@yjsgames/cooldown";
import type { CrashlyticsPort } from "@yjsgames/crashlytics-ports";
import type { ModalHost } from "../ui/ModalHost.js";
import type { TabRefresher } from "../ui/TabRefresher.js";
import type { ToastPresenter } from "../ui/ToastPresenter.js";
import type { ShellFrame } from "../ui/ShellFrame.js";
import type { TabRouter } from "../app/TabRouter.js";
import type { BattlefieldRunner } from "./BattlefieldRunner.js";
import type { TutorialRunner } from "@yjsgames/phaser-tutorial";
import type { ProgressState } from "../app/saveSwap.js";
import { getTextDirection } from "../i18n/i18n.js";
import type { TargetFpsSetting } from "@yjsgames/perf";
import type { DialogueTrigger } from "../data/dialogueData.js";

export interface GameSceneShellDeps {
  readonly scene: Phaser.Scene;
  readonly ctx: GameContext;
  readonly features: FeatureFlags;
  readonly modals: ModalHost;
  readonly controllers: () => DomainControllers;
  readonly replaceControllers: (next: DomainControllers) => void;
  readonly progress: () => ProgressState;
  readonly setBoxResult: (message: string) => void;
  readonly pieceRng: () => PieceRandomSource;
  readonly cheatForceHouseAd: () => boolean;
  readonly toggleForceHouseAd: () => boolean;
  readonly dailyReset: () => DailyResetService;
  readonly cooldown: () => CooldownService;
  readonly readinessCanRefresh: () => boolean;
  readonly tabs: TabRefresher;
  readonly frame: () => ShellFrame;
  readonly runner: () => BattlefieldRunner;
  readonly applySpaceshipVisualScale: () => void;
  readonly tutorialRunner: () => TutorialRunner | undefined;
  readonly tabRouter: () => TabRouter;
  readonly toasts: ToastPresenter;
  readonly registryGet: (key: string) => unknown;
  readonly toSaveData: () => ModuleBrickSaveData;
  readonly invalidateUi: () => void;
  readonly refreshTabLocks: () => void;
  readonly forceDialogueTrigger: (trigger: DialogueTrigger) => void;
  readonly refreshTabRedDots: () => void;
  readonly refreshActiveTab: () => void;
  readonly refreshAll: () => void;
  readonly refreshHud: () => void;
  readonly persist: () => void;
  readonly persistNow: () => void;
  readonly toggleBannerPreview: () => boolean;
  readonly bannerPreviewEnabled: () => boolean;
  readonly triggerChapterDefeat: () => void;
  readonly previewRebirthTransition: () => void;
  readonly toggleAppTimerOverlay: () => boolean;
  readonly appTimerOverlayEnabled: () => boolean;
  readonly applyTargetFps: (setting: TargetFpsSetting) => void;
  readonly pauseGameplayForAd: () => void;
  readonly resumeGameplayForAd: () => void;
}

/** GameScene 전용 ShellSystems adapter. 도메인 커맨드 조립 책임을 씬 본문에서 분리한다. */
export function createGameSceneShellSystems(deps: GameSceneShellDeps): ShellSystems {
  return createShellSystems({
    ctx: deps.ctx,
    features: deps.features,
    modals: deps.modals,
    screen: () => ({ width: deps.scene.scale.width, height: deps.scene.scale.height }),
    schedule: (cb, ms) => {
      const ev = deps.scene.time.delayedCall(ms, cb);
      return () => ev.remove(false);
    },
    adFree: () => deps.progress().adFree,
    setAdFree: (v) => {
      deps.progress().adFree = v;
    },
    discovered: () => deps.progress().discovered,
    setBoxResult: deps.setBoxResult,
    pieceRng: deps.pieceRng,
    cheatForceHouseAd: deps.cheatForceHouseAd,
    toggleForceHouseAd: deps.toggleForceHouseAd,
    dailyReset: deps.dailyReset,
    cooldown: deps.cooldown,
    shopDailyAdDiamondClaimed: () => deps.progress().shopDailyAdDiamondClaimed,
    setShopDailyAdDiamondClaimed: (v) => {
      deps.progress().shopDailyAdDiamondClaimed = v;
    },
    loadRawSave: () => deps.controllers().saveSystem.loadRaw(),
    invalidateUi: deps.invalidateUi,
    refreshTabLocks: deps.refreshTabLocks,
    forceDialogueTrigger: deps.forceDialogueTrigger,
    persist: deps.persist,
    persistNow: deps.persistNow,
    reloadApp: () => window.location.reload(),
    toggleBannerPreview: deps.toggleBannerPreview,
    bannerPreviewEnabled: deps.bannerPreviewEnabled,
    saveNow: () => void deps.controllers().saveSystem.save(deps.toSaveData()),
    canSave: deps.readinessCanRefresh,
    onUiFlush: () => {
      if (!deps.readinessCanRefresh()) return;
      if (deps.modals.isAnyModalOpen()) return;
      deps.tabs.refresh("battle");
      deps.refreshActiveTab();
      deps.refreshTabRedDots();
    },
    refreshAll: deps.refreshAll,
    showToast: (m) => deps.toasts.showToast(m),
    resetFireTimer: () => deps.runner().resetFireTimer(),
    applySpaceshipVisualScale: deps.applySpaceshipVisualScale,
    notifyModulePlaced: () => deps.tutorialRunner()?.notifyAction("module.placed"),
    setForceMysteryBricks: (e) => deps.runner().setForceMysteryBricks(e),
    setForceSpecialBricks: (e) => deps.runner().setForceSpecialBricks(e),
    forceCrash: () =>
      (deps.registryGet("crashlytics") as CrashlyticsPort | undefined)?.recordException(
        "FORCE_CRASH (manual QA)",
      ),
    triggerChapterDefeat: deps.triggerChapterDefeat,
    previewRebirthTransition: deps.previewRebirthTransition,
    toggleAppTimerOverlay: deps.toggleAppTimerOverlay,
    appTimerOverlayEnabled: deps.appTimerOverlayEnabled,
    resetBattlefield: () => {
      deps.runner().clearField();
      deps.runner().buildChapterGrid();
    },
    transitionChapterGrid: () => deps.runner().transitionChapterGrid(),
    replaceChapter: (next) => {
      deps.replaceControllers({ ...deps.controllers(), chapter: next });
    },
    onLanguageApplied: (code, dirChanged) => {
      if (dirChanged) {
        const dir = getTextDirection(code);
        deps.tabs.relayoutAll(dir);
        deps.frame().hud.relayout(dir);
        deps.frame().bottomTab.relayout(dir);
      }
      deps.frame().bottomTab.applyLanguage();
      deps.frame().guideQuestPanel.applyLanguage();
      deps.tabs.applyLanguageAll();
      deps.modals.applyLanguageToSettings(code);
      deps.modals.applyLanguageToModuleInfo();
      deps.modals.applyLanguageToSynergyInfo();
      deps.refreshHud();
      deps.refreshActiveTab();
    },
    onNumberFormatApplied: (format) => {
      deps.modals.setSettingsNumberFormat(format);
      deps.invalidateUi();
    },
    onTargetFpsApplied: deps.applyTargetFps,
    activeTab: () => deps.tabRouter().current(),
    settingsOpen: () => deps.modals.settingsOpen,
    pauseGameplayForAd: deps.pauseGameplayForAd,
    resumeGameplayForAd: deps.resumeGameplayForAd,
  });
}
